// Raise a clinical report about an incident — a refusal, a medication error, a
// needlestick — for the Compliance Manager to work.
//
// Prefilled from the incident *as saved* (GET /incidents/{id}/clinical-report-draft):
// the type its own answers point to, when it happened, the patient, and its key
// fields as plain text, which the raiser may trim. What they add is their account
// of why they're raising it, which is required. The event, the incident link and the
// affected person are taken from the incident server-side, never from this form.
//
// Raising one returns only its reference. The report is then listed on the
// incident, and its raiser may open it to read (core/clinical_reports.may_read).
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Typography, Alert, CircularProgress, Box,
} from "@mui/material";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { CLINICAL_HARM_LEVELS, CLINICAL_REPORT_TYPES } from "../constants";
import { fromLocalInput, toLocalInput } from "../clinicalReportRules";
import { useFullScreenDialog } from "../useFullScreenDialog";
import HintedSelect from "./HintedSelect";

const INCIDENTS_API = `${API_BASE_URL}/incidents`;

const EMPTY = { report_type: "", type_other: "", occurred_at: "", harm: "", account: "", extract: "" };

export default function RaiseClinicalReportDialog({ open, onClose, incidentId }) {
  const fullScreen = useFullScreenDialog();
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [raised, setRaised] = useState(null);  // the reference, once raised

  // Held in a ref so a caller passing a fresh arrow each render doesn't re-run the
  // fetch below — which would wipe whatever has been typed.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open || incidentId == null) return;
    let live = true;
    setDraft(null); setRaised(null); setForm(EMPTY);
    axios.get(`${INCIDENTS_API}/${incidentId}/clinical-report-draft`)
      .then(res => {
        if (!live) return;
        const d = res.data;
        setDraft(d);
        setForm({
          ...EMPTY,
          report_type: d.suggested_type || "",
          occurred_at: toLocalInput(d.occurred_at),
          extract: d.extract || "",
        });
      })
      .catch(() => live && closeRef.current());  // the interceptor has already said why
    return () => { live = false; };
  }, [open, incidentId]);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const setEv = (k) => (e) => set(k)(e.target.value);

  const otherMissing = form.report_type === "Other" && !form.type_other.trim();
  const canSubmit = !!draft && !!form.report_type && !otherMissing
    && !!form.account.trim() && !busy;

  const submit = () => {
    setBusy(true);
    axios.post(`${INCIDENTS_API}/${incidentId}/clinical-report`, {
      report_type: form.report_type,
      type_other: form.report_type === "Other" ? form.type_other : null,
      occurred_at: fromLocalInput(form.occurred_at),
      harm: form.harm || null,
      account: form.account,
      extract: form.extract,
    })
      .then(res => {
        setRaised(res.data.reference);
        notify(`Clinical report ${res.data.reference} raised.`, "success");
      })
      .catch(() => {})  // the interceptor toasts it; the form stays as typed
      .finally(() => setBusy(false));
  };

  const patient = draft && [draft.affected_name, draft.affected_race_number && `#${draft.affected_race_number}`]
    .filter(Boolean).join(" ");

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>Raise a clinical report</DialogTitle>
      <DialogContent dividers>
        {raised ? (
          <Alert severity="success">
            Clinical report <strong>{raised}</strong> has been raised for the Compliance Manager.
            It's listed on the incident, where you can open it to read.
          </Alert>
        ) : !draft ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              For something about this incident that needs following up — a refusal of
              treatment, a medication error, a safeguarding concern. It goes to the
              Compliance Manager, and is listed on the incident.
            </Typography>
            <Typography variant="body2">
              Person affected: {patient || <em>no patient recorded on the incident</em>}
            </Typography>
            <HintedSelect label="Type" options={CLINICAL_REPORT_TYPES} value={form.report_type}
                          onChange={set("report_type")} allowBlank={false} required
                          helperText={draft.suggested_type
                            ? "Suggested from the incident — change it if it's something else."
                            : "What kind of concern this is."} />
            {form.report_type === "Other" && (
              <TextField label="Type of report" value={form.type_other} onChange={setEv("type_other")}
                         required error={otherMissing} />
            )}
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
              <TextField type="datetime-local" label="When it happened" value={form.occurred_at}
                         onChange={setEv("occurred_at")}
                         slotProps={{ inputLabel: { shrink: true } }}
                         helperText="Set to when the incident was raised." />
              <HintedSelect label="Degree of harm" options={CLINICAL_HARM_LEVELS} value={form.harm}
                            onChange={set("harm")} helperText="Leave as not set if you're unsure." />
            </Box>
            <TextField label="What happened, and why you're raising this" value={form.account}
                       onChange={setEv("account")} multiline minRows={4} required autoFocus
                       helperText="In your own words: what happened, what concerns you, and what was done about it at the time." />
            <TextField label="From the incident" value={form.extract} onChange={setEv("extract")}
                       multiline minRows={6}
                       helperText="Copied from the incident as last saved, and included with the report. Edit or trim it as you need." />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {raised ? (
          <Button variant="contained" onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={!canSubmit}>
              {busy ? "Raising…" : "Raise report"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
